
  import { defineComponent } from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  import { TFilterSelect, TFilterTreeSelect } from '@/common/components/redesigned/Filter/types/filter';
  import { MenuModes } from '@/common/components/redesigned/TreeMultiselect/types';
  import constants from '@/common/constants';
  import { MENU_GETTERS_NAME } from '@/common/store/menu/name';
  import { TDictionary, TTextDictionary } from '@/common/types/dictionary';
  import { TPortalNavigationMenu } from '@/common/types/menu';
  import { TDictionaryArrayAxiosResponse } from '@/common/types/response';

  import { USERS_ACTIONS_NAME, USERS_GETTERS_NAME } from '@/modules/admin/store/users/name';
  import { TUserItem } from '@/modules/admin/types/users';
  import { filterMinekOrganization } from '@/modules/admin/utils/statistics';

  import API_DEP from '../../../api/department';
  import API_ORG from '../../../api/organization';
  import { OrganizationTypes, TOrganizationType } from '../../../types/organizations';
  import { TStatisticsPortalFilter } from '../../../types/statistics/statistics-portal';
  import StatisticsFilter from '../StatisticsFilter.vue';

  type TPartialFilterSelect<T extends object = object> = Omit<TFilterSelect<T>, 'type' | 'label' | 'placeholder'>;

  export default defineComponent({
    name: 'StatisticsPortalFilter',
    components: { StatisticsFilter },
    emits: {
      searchFilter: (value: TStatisticsPortalFilter) => value !== undefined,
    },
    data(): {
      departments: TTextDictionary[];
      organizations: TDictionary[];
      usersGroups: TOrganizationType[];
      filter: TStatisticsPortalFilter;
    } {
      return {
        departments: [],
        organizations: [],
        usersGroups: [],
        filter: {},
      };
    },
    created() {
      this.getDictionaries();
    },
    computed: {
      ...mapGetters({
        users: USERS_GETTERS_NAME.getUsers,
        menu: MENU_GETTERS_NAME.getMenu,
      }),
      getDepartmentFilterConfig(): TPartialFilterSelect<TDictionary> {
        return {
          options: this.departments.map((department) => ({
            id: department.id,
            name: department.text,
          })),
          valueProp: 'id',
          labelProp: 'name',
          vModel: 'departmentId',
          disabled: this.filter.userGroupId !== OrganizationTypes.MEP_DEPARTMENT,
          clearable: true,
        };
      },
      getOrganizationFilterConfig(): TPartialFilterSelect<TDictionary> {
        return {
          options: this.organizations,
          valueProp: 'id',
          labelProp: 'name',
          vModel: 'organizationId',
          disabled: this.filter.userGroupId !== OrganizationTypes.FOIV,
          clearable: true,
        };
      },
      getOrganizationDepartmentFilterConfig() {
        const userGroupId = this.filter.userGroupId;
        return userGroupId === OrganizationTypes.FOIV
          ? this.getOrganizationFilterConfig
          : this.getDepartmentFilterConfig;
      },
      getFiltersConfig(): [
        TFilterSelect<TUserItem>,
        TFilterSelect<TDictionary>,
        TFilterSelect<TDictionary>,
        TFilterTreeSelect<TPortalNavigationMenu>,
      ] {
        return [
          {
            type: 'select',
            label: 'Пользователь',
            placeholder: 'Все',
            vModel: 'userId',
            options: this.users,
            valueProp: 'userId',
            labelProp: 'fullName',
            clearable: true,
          },
          {
            type: 'select',
            label: 'Группа пользователей',
            options: this.usersGroups,
            valueProp: 'id',
            labelProp: 'name',
            vModel: 'userGroupId',
            placeholder: 'Все',
            clearable: true,
          },
          {
            type: 'select',
            label: 'Организация/Департамент',
            placeholder: 'Все',
            ...this.getOrganizationDepartmentFilterConfig,
          },
          {
            type: 'tree-select',
            label: 'Подсистема/Страница',
            options: this.menu,
            valueProp: 'id',
            labelProp: 'name',
            childrenProp: 'items',
            vModel: 'sectionId',
            isSingleMode: true,
            placeholder: 'Все',
            menuMode: MenuModes.NONE,
          },
        ];
      },
    },
    methods: {
      ...mapActions({
        getUsers: USERS_ACTIONS_NAME.getUsers,
      }),
      searchFilter(filter: TStatisticsPortalFilter) {
        this.$emit('searchFilter', filter);
      },
      updateFilter(filter: TStatisticsPortalFilter) {
        this.filter = filter;
      },
      async getDepartments() {
        const response = await API_DEP.getDepartmentsAsSelectionList();
        this.departments = response.data;
      },
      async getOrganizations() {
        const response: TDictionaryArrayAxiosResponse = await API_ORG.search({
          ...constants.paramsFullListDefault,
          typeIds: [constants.organizationType.foiv], // Чтобы грузились только организации ФОИВ
        });
        this.organizations = response.data.items.filter(filterMinekOrganization);
      },
      async getUsersGroups() {
        this.usersGroups = [
          {
            id: OrganizationTypes.FOIV,
            name: 'ФОИВ (кроме МЭР)',
          },
          {
            id: OrganizationTypes.MEP_DEPARTMENT,
            name: 'Департамент МЭР',
          },
          {
            id: OrganizationTypes.ZAGRANAPARAT,
            name: 'Загранаппарат',
          },
          {
            id: OrganizationTypes.RF_SUBJECT,
            name: 'Субъект РФ',
          },
        ];
      },
      getDictionaries() {
        this.getDepartments();
        this.getOrganizations();
        this.getUsersGroups();
        this.getUsers();
      },
    },
  });
